import {
  Alert,
  Button,
  DialogActions,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";

import { normalizeErrorMessages } from "api/common/utils/rtkq.utils";
import {
  useCreateMailUserMutation,
  useGetMailConfigQuery,
  useListMailListsQuery,
} from "api/memberPortal/mailList/mailList.api";
import { MailingListDomains } from "api/memberPortal/mailList/mailList.types";
import { useAppForm } from "components/Forms/AppForm";
import SearchBar from "components/MUI/DataGrid/Detached/SearchBar";
import CreateMemberFields from "features/memberPortal/mail/components/MailListMembers/components/MailListCreateMember/CreateNewMember.fields";

import ToggleChip from "../MailListMemberDetails/components/ToggleChip";
import ToggleChipGroup from "../MailListMemberDetails/components/ToggleChipGroup";
import MailListMembersList from "../MailListMemberDetails/MailListMembersList.list";

import {
  createNewMemberSchema,
  CreateNewMemberSchemaValues,
} from "./CreateNewMember.schema";
import SelectedMailListChips from "./SelectedMailLists.chips";

interface CreateNewMemberFormProps {
  onClose: () => void;
}

export default function CreateNewMemberForm({
  onClose,
}: CreateNewMemberFormProps) {
  const [createUser, { isLoading, error }] = useCreateMailUserMutation();

  const memberForm = useAppForm({
    defaultValues: {
      name: "",
      email: "",
      mailListIds: [],
    } as CreateNewMemberSchemaValues,

    validators: {
      onSubmit: createNewMemberSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        await createUser({
          name: value.name,
          email: value.email,
          mailListIds: value.mailListIds,
        }).unwrap();

        onClose();
      } catch {
        // Swallow the rejection from .unwrap() — the failure is surfaced to the
        // user via the mutation's `error` (rendered in the Alert below). We only
        // catch to keep the modal open on failure instead of closing it.
      }
    },
  });

  const [searchValue, setSearchValue] = useState("");
  const [domainFilter, setDomainFilter] = useState<MailingListDomains | "">("");

  const { data: mailConfig } = useGetMailConfigQuery();
  const { data: mailLists, isLoading: mailListsLoading } =
    useListMailListsQuery({});

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const filteredLists = useMemo(() => {
    if (!mailLists) return [];

    const search = searchValue.trim().toLowerCase();

    return mailLists.filter((list) => {
      const matchesDomain = !domainFilter || list.domain === domainFilter;
      const matchesSearch = !search || list.name.toLowerCase().includes(search);
      return matchesDomain && matchesSearch;
    });
  }, [mailLists, searchValue, domainFilter]);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        memberForm.handleSubmit();
      }}
    >
      <Stack spacing={3} sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography
            variant="subtitle2"
            sx={{
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            Member Details
          </Typography>
          <CreateMemberFields
            form={memberForm}
            fields={{ name: "name", email: "email" }}
          />
        </Stack>
        <Divider />
        <Stack spacing={2}>
          <Typography
            variant="subtitle2"
            sx={{
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            Add Member to List
          </Typography>
          <SearchBar onSearchChange={handleSearchChange} />
          <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
            <Typography variant="body2">Filter: </Typography>
            <ToggleChipGroup
              value={domainFilter}
              onChange={(_e, val) => setDomainFilter(val)}
              exclusive
            >
              {(mailConfig?.domains ?? []).map((d) => (
                <ToggleChip key={d.label} value={d.label} label={d.label} />
              ))}
            </ToggleChipGroup>
          </Stack>
          {mailListsLoading || !mailLists ? (
            <Stack spacing={1}>
              <Skeleton variant="rounded" height={48} />
              <Skeleton variant="rounded" height={48} />
              <Skeleton variant="rounded" height={48} />
            </Stack>
          ) : (
            <memberForm.Field name="mailListIds">
              {(field) => (
                <>
                  <MailListMembersList
                    mailLists={filteredLists}
                    enableSelection
                    selectedIds={field.state.value}
                    onSelectionChange={field.handleChange}
                    collapsed
                  />
                  <SelectedMailListChips
                    selectedIds={field.state.value}
                    mailLists={mailLists}
                    onRemove={(id) =>
                      field.handleChange(
                        field.state.value.filter((s) => s !== id),
                      )
                    }
                  />
                </>
              )}
            </memberForm.Field>
          )}
        </Stack>
        {error && (
          <Alert severity="error" sx={{ py: 0 }}>
            {normalizeErrorMessages(error)}
          </Alert>
        )}
      </Stack>

      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" loading={isLoading}>
          Create
        </Button>
      </DialogActions>
    </form>
  );
}
